import { PlugZap } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { setEmbeddedSession } from '../api/client';
import { CapabilitiesProvider } from '../components/CapabilitiesProvider';
import { TenantTimeProvider } from '../components/TenantTimeProvider';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { CommsModule } from '../screens/comms/CommsModule';
import { CommsProvider } from '../screens/comms/CommsProvider';
import { makeEmbeddedSource, type CommsSource } from '../screens/comms/source';
import { CommsEmbedBridge, type EmbedInit } from './bridge';
import { type DialerHost, NO_HOST_DIALER } from './dialerState';
import { makeHostLatch } from './hostLatch';
import { NO_HOST_PHONE, type PhoneHost } from './phoneState';
import '../screens/comms/comms.css';

/** The zone the fixture data was authored in — see preview/fixtures.ts. */
const FIXTURE_ZONE = 'America/New_York';

/**
 * The dashboard-embedded Communications module: full-bleed (no console
 * chrome), authenticated by the parent frame over the postMessage bridge —
 * the agent never signs in twice. Deep-linkable per channel via
 * `/embed?tab=email|whatsapp|sms|telegram|website|calls|campaigns|settings`
 * (and `&c=<displayId>`, or `&to=<phone|email>` to open the conversation with
 * one person), or live via the CRM_COMMS_NAVIGATE message.
 *
 * `&tabs=collapsed` opens with the module's own channel bar folded to a sliver,
 * giving its 48px to the conversation — for a parent whose shell already offers
 * the same choices. The sliver unfolds it, so it is a default and not a lock.
 *
 * Dev-only: `/embed?demo=1` runs the same bridge handshake against the
 * fixture data set, so `public/embed-harness.html` can demo the whole
 * integration without a session.
 */
export function EmbedComms() {
  const [, setParams] = useSearchParams();
  const [session, setSession] = useState<EmbedInit | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [demoSource, setDemoSource] = useState<CommsSource | null>(null);
  const [liveSource, setLiveSource] = useState<CommsSource | null>(null);

  // Frozen at mount; never available in production builds.
  const demo = useMemo(
    () =>
      import.meta.env.DEV &&
      new URLSearchParams(window.location.search).get('demo') === '1',
    [],
  );

  // The demo source is built in a later effect than the one that owns the
  // bridge, and it is the only thing outside that effect that posts.
  const bridgeRef = useRef<CommsEmbedBridge | null>(null);
  const setParamsRef = useRef(setParams);
  useEffect(() => {
    setParamsRef.current = setParams;
  });

  useEffect(() => {
    const bridge = new CommsEmbedBridge();

    /**
     * The dashboard's own phones, fanned out to whoever is mounted.
     *
     * State is LATCHED rather than only forwarded: each frame reports on its
     * own schedule, and a subscriber that mounted between two reports would
     * otherwise sit on the empty default — no Call button, a sign-in bar for a
     * dialer that is signed in — until the next call happened to change
     * something.
     */
    const phoneLatch = makeHostLatch(NO_HOST_PHONE);
    const dialerLatch = makeHostLatch(NO_HOST_DIALER);
    bridge.onPhoneState = phoneLatch.publish;
    bridge.onDialerState = dialerLatch.publish;
    const phoneHost: PhoneHost = {
      subscribe: phoneLatch.subscribe,
      send: (command) => bridge.sendPhoneCommand(command),
    };
    const dialerHost: DialerHost = {
      subscribe: dialerLatch.subscribe,
      send: (command) => bridge.sendDialerCommand(command),
    };

    bridge.onInit = (init) => {
      // A second INIT is a new session — the dashboard signed in as somebody
      // else, or another tenant. The latches would otherwise hand the previous
      // tenant's call, and the display id of its thread, to the next tenant's
      // call bar for a beat.
      phoneLatch.publish(NO_HOST_PHONE);
      dialerLatch.publish(NO_HOST_DIALER);
      if (!demo) {
        setEmbeddedSession({
          tenant: init.tenant,
          getToken: bridge.getToken,
          onAuthFailed: () => bridge.notifyAuthFailed(),
        });
        setLiveSource({
          ...makeEmbeddedSource(
            init.tenant,
            bridge.getToken,
            (payload) => bridge.notifyNotification(payload),
            (event, payload) => bridge.notifyCall(event, payload),
            // Only where the parent says it holds the phone. An older dashboard
            // never sets the flag, and there this module's own device is still
            // the only one the agent has. The dialer follows the same rule.
            init.phoneHost ? phoneHost : null,
            init.dialerHost ? dialerHost : null,
            // Same rule once more: only a dashboard that says it can open a
            // record gets asked to. Where it cannot, the panel keeps the link
            // out to the dashboard that it had.
            init.profileHost ? (profile) => bridge.openProfile(profile) : undefined,
            // And for a booking, behind a flag of its own: a dashboard that
            // opens profiles may still predate OPEN_JOB.
            init.jobHost ? (job) => bridge.openJob(job) : undefined,
            // Unconditional, like NOTIFICATION: a dashboard that does not
            // listen for it loses nothing.
            (ids) => bridge.notifyNotificationsRead(ids),
          ),
          // A dashboard with its own website pages keeps Analytics there.
          websiteHost: init.websiteHost,
        });
      }
      // The answer to the flags INIT carried, so the parent knows this module
      // will not ring for itself where it said it holds the phone. The demo
      // defers nothing (it keeps its fixture phones), and says so.
      bridge.notifyHosted({
        phone: !demo && init.phoneHost,
        dialer: !demo && init.dialerHost,
      });
      setSession(init);
      setTheme(init.theme);
    };
    bridge.onTheme = setTheme;
    bridge.onNavigate = ({ tab, conversationId, to, tabs }) => {
      setParamsRef.current((current) => {
        const draft = new URLSearchParams(current);
        if (tab) {
          draft.set('tab', tab);
          draft.delete('c');
        }
        // Written into the URL like every other instruction from the parent; the
        // module acts on it when it CHANGES, so re-stating the same answer never
        // overrules the fold button (see the `?tabs=` note in CommsModule). An
        // absent one leaves the reel exactly as the agent last left it.
        if (tabs !== undefined) draft.set('tabs', tabs);
        if (conversationId !== undefined) draft.set('c', String(conversationId));
        // A person named by address, from a contact icon somewhere in the
        // dashboard. The module clears the parameter once it has resolved
        // them, so this is a request rather than a location — and an arriving
        // `to` drops whatever conversation the last one landed on.
        if (to !== undefined) {
          draft.set('to', to);
          draft.delete('c');
        }
        return draft;
      });
    };
    bridgeRef.current = bridge;
    bridge.start();

    return () => {
      bridgeRef.current = null;
      bridge.dispose();
      phoneLatch.clear();
      dialerLatch.clear();
      if (!demo) setEmbeddedSession(null);
    };
  }, [demo]);

  useEffect(() => {
    // The literal DEV check first: in production builds this whole body is
    // statically dead, so the fixtures chunk is never even emitted.
    if (!import.meta.env.DEV) return;
    if (!demo || !session || demoSource) return;
    let cancelled = false;
    void import('../screens/comms/preview/fixtures').then((m) => {
      // `phoneHost`/`dialerHost` are deliberately NOT honoured here: the demo
      // has no session to mint a voice token with, so deferring to a real
      // phone frame would demo a module with no Twilio route at all. Fixtures
      // keep their own.
      // `profileHost` and `jobHost` ARE honoured, unlike the phones: opening a
      // record costs the demo nothing and posts the real message, which is how
      // the harness stays the reference implementation for the Flutter parent.
      if (!cancelled) {
        setDemoSource({
          ...m.makeFixtureSource(),
          embedded: true,
          openProfile: session.profileHost
            ? (profile) => bridgeRef.current?.openProfile(profile)
            : undefined,
          openJob: session.jobHost ? (job) => bridgeRef.current?.openJob(job) : undefined,
          // Relayed as the live module relays it, so opening a conversation in
          // the harness shows the read the dashboard's bell is told about.
          notifyHostRead: (ids) => bridgeRef.current?.notifyNotificationsRead(ids),
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [demo, session, demoSource]);

  const source = demo ? demoSource : liveSource;

  if (!session || !source) {
    return (
      <div className="comms-embed-host" data-theme={theme}>
        <div className="comms" style={{ height: '100%' }}>
          <div className="comms-locked">
            <div className="comms-locked-inner">
              <div className="comms-locked-icon">
                <PlugZap size={28} />
              </div>
              <div className="comms-locked-title">Waiting for the dashboard…</div>
              <div className="comms-locked-body">
                This page runs inside the dispatch dashboard, which signs it in automatically.
                Opened it directly? Use <a href="/comms">the standalone console</a> instead.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="comms-embed-host" data-theme={theme}>
      {/* Demo runs without a session, so it cannot fetch /meta — fixtures pin the zone. */}
      <TenantTimeProvider zone={demo ? FIXTURE_ZONE : undefined}>
        <CapabilitiesProvider capabilities={demo ? null : undefined}>
          <CommsProvider source={source} identity={{ email: session.email }}>
            {/* The embed has no shell above it to catch a render crash, and a
                white iframe inside the dashboard is the least explicable of
                all the ways this can fail. */}
            <ErrorBoundary label="Communications">
              <CommsModule />
            </ErrorBoundary>
          </CommsProvider>
        </CapabilitiesProvider>
      </TenantTimeProvider>
    </div>
  );
}
